import React, { useState, useEffect } from 'react';

const Portfolio = () => {
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const Card = ({ children, className = "" }) => (
    <div className={`group relative ${className}`}>
      <div className="bg-gradient-to-br from-slate-800/90 via-blue-900/30 to-slate-800/90 backdrop-blur-xl rounded-2xl border border-cyan-400/30 hover:border-cyan-400/60 transition-colors duration-300">
        {children}
      </div>
    </div>
  );

  const TechCard = ({ icon, title, description }) => (
    <Card className="hover:scale-105 transition-transform duration-300">
      <div className="p-8">
        <div className="w-16 h-16 bg-gradient-to-br from-cyan-400 via-blue-500 to-purple-600 rounded-full flex items-center justify-center text-2xl mx-auto mb-6">
          {icon}
        </div>
        <h3 className="text-xl font-semibold text-cyan-400 mb-4 text-center">{title}</h3>
        <p className="text-gray-300 text-center leading-relaxed">{description}</p>
      </div>
    </Card>
  );

  const ProjectCard = ({ emoji, title, description, tags }) => (
    <Card className="hover:scale-105 transition-transform duration-300">
      <div className="h-48 bg-gradient-to-br from-cyan-400 via-blue-500 to-purple-600 flex items-center justify-center text-6xl">
        <span>{emoji}</span>
      </div>
      <div className="p-8">
        <h3 className="text-xl font-semibold text-cyan-400 mb-4">{title}</h3>
        <p className="text-gray-300 mb-6 leading-relaxed">{description}</p>
        <div className="flex flex-wrap gap-2">
          {tags.map((tag, tagIndex) => (
            <span 
              key={tagIndex} 
              className="bg-gradient-to-r from-cyan-400/20 to-blue-500/20 text-cyan-400 px-3 py-1 rounded-full text-sm border border-cyan-400/30"
            >
              {tag}
            </span>
          ))}
        </div>
      </div>
    </Card>
  );

  const ContactCard = ({ icon, title, description }) => (
    <Card className="hover:scale-105 transition-transform duration-300">
      <div className="p-8 text-center">
        <div className="text-4xl mb-4">{icon}</div>
        <h3 className="text-lg font-semibold text-cyan-400 mb-3">{title}</h3>
        <p className="text-gray-300">{description}</p>
      </div>
    </Card>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white overflow-x-hidden">
      
      <style jsx>{`
        .gradient-text {
          background: linear-gradient(45deg, #06b6d4, #3b82f6, #8b5cf6, #ec4899);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
      `}</style>

      {/* Navigation */}
      <nav 
        className={`fixed top-0 w-full z-50 transition-all duration-300 ${
          scrollY > 100 
            ? 'bg-black/80 backdrop-blur-xl border-b border-cyan-400/30' 
            : 'bg-black/40 backdrop-blur-lg'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <div className="text-2xl font-bold gradient-text">
              Your Name
            </div>
            <div className="hidden md:flex space-x-8">
              {['Home', 'About', 'Skills', 'Projects', 'Contact'].map((item) => (
                <a
                  key={item}
                  href={`#${item.toLowerCase()}`}
                  className="relative text-white hover:text-cyan-400 transition-colors duration-300 font-medium group"
                >
                  {item}
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-cyan-400 to-purple-500 group-hover:w-full transition-all duration-300"></span>
                </a>
              ))}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="min-h-screen flex items-center justify-center relative">
        <div className="text-center z-10 px-6 max-w-4xl mx-auto">
          <h1 className="text-6xl md:text-8xl font-bold mb-6 gradient-text">
            Your Name
          </h1>
          <div className="text-2xl md:text-3xl text-cyan-400 font-semibold mb-6">
            React Native Developer
          </div>
          <p className="text-xl md:text-2xl mb-10 text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Crafting exceptional cross-platform mobile experiences with React Native. 
            Transforming ideas into powerful, scalable mobile applications that users love.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
            <a
              href="#projects"
              className="px-10 py-4 bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-600 text-white rounded-full font-semibold hover:scale-105 transition-transform duration-300"
            >
              View My Work
            </a>
            <a
              href="#contact"
              className="px-10 py-4 border-2 border-cyan-400 text-cyan-400 rounded-full font-semibold hover:bg-cyan-400/10 hover:scale-105 transition-all duration-300"
            >
              Let's Connect
            </a>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-32 bg-black/30 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-5xl font-bold text-center mb-16 gradient-text relative">
            About Me
            <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-24 h-1 bg-gradient-to-r from-cyan-400 to-purple-500 rounded-full"></div>
          </h2>
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="space-y-6">
              <h3 className="text-2xl font-semibold text-cyan-400 mb-4">
                Passionate Mobile Developer
              </h3>
              <p className="text-lg text-gray-300 leading-relaxed">
                I'm a dedicated React Native developer with a passion for creating 
                seamless mobile experiences. I specialize in building cross-platform 
                applications that deliver native performance and user satisfaction.
              </p>
              <p className="text-lg text-gray-300 leading-relaxed">
                My expertise spans the entire mobile development lifecycle, from concept 
                and design to deployment and maintenance. I focus on clean code, 
                modern architecture patterns, and performance optimization.
              </p>
              <p className="text-lg text-gray-300 leading-relaxed">
                When I'm not coding, I'm exploring the latest mobile technologies, 
                contributing to open-source projects, and sharing knowledge with 
                the developer community.
              </p>
            </div>
            <div className="flex justify-center">
              <div className="w-80 h-80 bg-gradient-to-br from-cyan-400 via-blue-500 to-purple-600 rounded-3xl flex items-center justify-center hover:scale-105 transition-transform duration-300">
                <div className="text-8xl font-bold text-white">
                  👨‍💻
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Skills Section */}
      <section id="skills" className="py-32">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-5xl font-bold text-center mb-16 gradient-text relative">
            My Skills
            <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-24 h-1 bg-gradient-to-r from-cyan-400 to-purple-500 rounded-full"></div>
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: "⚛️", title: "React Native", description: "Expert-level proficiency in React Native framework, creating seamless cross-platform mobile applications with native performance and user experience." },
              { icon: "📱", title: "iOS & Android", description: "Native platform integration, custom modules, and platform-specific optimizations for both iOS and Android ecosystems." },
              { icon: "⚡", title: "Performance", description: "Advanced optimization techniques including code splitting, lazy loading, and efficient state management for lightning-fast apps." },
              { icon: "🔧", title: "Modern Tools", description: "Expo, TypeScript, Redux Toolkit, React Query, Firebase, and the latest development tools and libraries in the React Native ecosystem." },
              { icon: "🎨", title: "UI/UX Design", description: "Beautiful, intuitive interfaces using modern design systems, smooth animations, and responsive layouts that delight users." },
              { icon: "☁️", title: "Backend Integration", description: "Seamless API integration, real-time data synchronization, and cloud services integration for complete mobile solutions." }
            ].map((skill, index) => (
              <TechCard key={index} {...skill} />
            ))}
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section id="projects" className="py-32 bg-black/30 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-5xl font-bold text-center mb-16 gradient-text relative">
            Featured Projects
            <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-24 h-1 bg-gradient-to-r from-cyan-400 to-purple-500 rounded-full"></div>
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10">
            {[
              {
                emoji: "🏪",
                title: "E-Commerce Mobile App",
                description: "Full-featured e-commerce platform with real-time inventory, secure payments, and seamless user experience across iOS and Android.",
                tags: ['React Native', 'Redux', 'Stripe', 'Firebase']
              },
              {
                emoji: "💬",
                title: "Social Chat Platform",
                description: "Real-time messaging app with video calls, media sharing, and advanced security features. Supports thousands of concurrent users.",
                tags: ['React Native', 'WebRTC', 'Socket.io', 'Node.js']
              },
              {
                emoji: "📊",
                title: "Analytics Dashboard",
                description: "Business intelligence mobile app with interactive charts, real-time data visualization, and comprehensive reporting features.",
                tags: ['React Native', 'D3.js', 'GraphQL', 'MongoDB']
              }
            ].map((project, index) => (
              <ProjectCard key={index} {...project} />
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-32">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h2 className="text-5xl font-bold mb-8 gradient-text relative">
            Let's Build Something Amazing
            <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-24 h-1 bg-gradient-to-r from-cyan-400 to-purple-500 rounded-full"></div>
          </h2>
          <p className="text-xl text-gray-300 mb-12 max-w-3xl mx-auto leading-relaxed">
            Ready to transform your mobile app idea into reality? Let's discuss how I can 
            bring your vision to life with cutting-edge React Native development.
          </p>
          
          <div className="grid md:grid-cols-3 gap-8 mt-16">
            {[
              { icon: "📧", title: "Email Me", description: "Drop me a line and I'll get back to you within 24 hours." },
              { icon: "💼", title: "Project Discussion", description: "Let's talk about your next React Native project and how I can help you succeed." },
              { icon: "⚡", title: "Quick Response", description: "I typically respond to project inquiries and collaboration requests quickly." }
            ].map((contact, index) => (
              <div key={index} className={`animate-fadeInUp`} style={{ animationDelay: `${index * 0.2}s` }}>
                <ContactCard {...contact} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-black/80 backdrop-blur-lg border-t border-cyan-400/30">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="text-2xl font-bold gradient-text mb-4 md:mb-0">
              Your Name
            </div>
            <div className="flex space-x-8">
              {[
                { href: "#projects", text: "Portfolio" },
                { href: "#skills", text: "Services" },
                { href: "#contact", text: "Contact" }
              ].map((link) => (
                <a 
                  key={link.text}
                  href={link.href} 
                  className="text-gray-400 hover:text-cyan-400 transition-colors duration-300"
                >
                  {link.text}
                </a>
              ))}
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-gray-700/50 text-center text-gray-400">
            <p>© 2024 Your Name. Crafting exceptional React Native experiences.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Portfolio;